/**
 * La télémétrie du moteur : ce que chaque machine d'états laisse derrière elle dans Bugsnag.
 *
 * Trois gestes, un par engagement de fiabilité :
 *  - `transition` : un fil d'Ariane à chaque transition de la machine (`live: connecting → connected`),
 *    gardé aussi dans le fil propre à la machine, que chaque erreur emporte ;
 *  - `error` : une erreur typée (`EngineError`), avec la stack de sa cause, son étape et son contexte
 *    (machine, rôle, plateforme, version de l'app, fil des dernières transitions), envoyée à Bugsnag
 *    et rendue à l'appelant — un `catch` du moteur n'est jamais vide, il appelle `error` ;
 *  - `startOutage` : une coupure qui commence, puis `end()` qui la ferme et rend sa mesure (début,
 *    fin, durée), pour que l'écran ou le moteur la transmette au serveur. Les deux bouts laissent un
 *    fil d'Ariane.
 *
 * **Jamais d'id ni de nom d'élève dans le message d'une erreur** : il ne se compose que de la machine,
 * de l'étape et d'un code HTTP, des mots du code. Ce qui décrit un cas va dans les métadonnées, et
 * une clé qui porterait un nom (`name`, `firstname`, `email`, `phone`…) y est retirée avant l'envoi.
 * Le message de la cause y est gardé quand c'est une `Error` (celui d'une bibliothèque : « Network
 * Error »), ou un objet nommé comme une classe d'erreur qui n'en est pas une (le `MediaStreamError`
 * d'une caméra qui ne s'ouvre pas, `describeNonError`) ; ni d'une chaîne ni d'un autre objet. Il peut porter ce
 * qu'un serveur a écrit : une erreur de connexion de LiveKit met dans son message le corps du 401 ou
 * du 403 de son serveur. Un jeton qui s'y trouverait (un JWT, un `access_token=`) est masqué, et le
 * message est coupé à `CAUSE_MESSAGE_MAX` caractères (`causeMessageOf`, `nonError.ts`). Le corps
 * d'une réponse de notre API, lui, n'y entre pas (une erreur axios ne dit que son code). Un moteur
 * ne fabrique jamais une `Error` dont le message porterait un nom.
 *
 * Le moteur n'importe ni `common/**` ni `notifyBugsnag` : le Bugsnag de chaque plateforme passe par
 * `bugsnagClient{,.web}.ts`, de même signature. En développement, rien ne part.
 */
import Constants from "expo-constants"
import { Platform } from "react-native"

import { bugsnagClient } from "./bugsnagClient"
import { causeMessageOf, describeNonError } from "./nonError"
import { type TelemetryClient, type TelemetryMetadata, type TelemetryValue } from "./telemetryClient"
import { cleanMetadata, TransitionTrail, type TelemetryData } from "./telemetryMetadata"

export type { TelemetryClient, TelemetryData, TelemetryMetadata, TelemetryValue }
export { cleanMetadata }

/** Qui fait tourner la machine : le moteur ne sert aujourd'hui que l'élève, le tuteur le reprendra. */
export type TelemetryRole = "student" | "tutor"

/** Une coupure fermée : quand elle a commencé, quand elle a fini, combien elle a duré. */
export type OutageMeasure = {
    kind: string
    /** ISO. */
    startedAt: string
    /** ISO. */
    endedAt: string
    durationMs: number
}

export type OutageHandle = {
    /** Ferme la coupure. Un second appel rend la même mesure, sans nouveau fil d'Ariane. */
    end: (data?: TelemetryData) => OutageMeasure
}

export type EngineTelemetry = {
    transition: (from: string, to: string, event: string, data?: TelemetryData) => void
    error: (step: string, cause: unknown, data?: TelemetryData) => EngineError
    startOutage: (kind: string, data?: TelemetryData) => OutageHandle
}

export type TelemetryOptions = {
    role: TelemetryRole
    /** Bugsnag par défaut ; un test passe le sien. */
    client?: TelemetryClient
    /** `Date.now` par défaut ; un test passe le sien. */
    now?: () => number
}

export { TRANSITION_TRAIL_SIZE } from "./telemetryMetadata"

/**
 * L'erreur d'un moteur : sa machine et son étape la nomment (`live: refresh`), sa cause la suit.
 * Le message ne dit que des mots du code, plus le code HTTP de la cause s'il y en a un.
 */
export class EngineError extends Error {
    readonly machine: string
    readonly step: string
    readonly status: number | null
    readonly cause: unknown

    constructor(machine: string, step: string, cause: unknown) {
        const status = httpStatusOf(cause)
        super(status != null ? `${machine}: ${step} (HTTP ${status})` : `${machine}: ${step}`)
        this.name = "EngineError"
        this.machine = machine
        this.step = step
        this.status = status
        this.cause = cause
        // La stack qui compte est celle de la cause : là où ça a cassé, pas là où on l'a nommé.
        const causeStack = cause instanceof Error ? cause.stack : undefined
        if (causeStack != null) {
            const frames = causeStack.split("\n").slice(1).join("\n")
            this.stack = frames !== "" ? `${this.name}: ${this.message}\n${frames}` : this.stack
        }
    }
}

/** Le code HTTP d'une cause : une erreur axios, ou l'échec qu'un réducteur d'API a rangé. */
export function httpStatusOf(cause: unknown): number | null {
    if (cause == null || typeof cause !== "object") return null
    const candidate = cause as { status?: unknown; response?: { status?: unknown } }
    const status = candidate.response?.status ?? candidate.status
    return typeof status === "number" ? status : null
}

/**
 * Ce qu'on sait d'une cause : son type, et son message sans jeton et borné (`causeMessageOf`). Une
 * cause qui n'est pas une `Error` (la caméra que react-native-webrtc n'ouvre pas) se décrit par
 * `describeNonError`.
 */
function describeCause(cause: unknown): TelemetryMetadata {
    if (cause instanceof Error) return { causeClass: cause.name, causeMessage: causeMessageOf(cause.message) }
    if (cause == null) return { causeClass: "none" }
    if (typeof cause !== "object") return { causeClass: typeof cause }
    const { kind, message } = describeNonError(cause)
    return message == null ? { causeClass: kind } : { causeClass: kind, causeMessage: causeMessageOf(message) }
}

function appVersion(): string | null {
    return Constants.expoConfig?.version ?? null
}

/**
 * La télémétrie d'une machine. Une instance par machine montée : elle tient le fil de ses propres
 * transitions, qu'une erreur emporte même quand les fils d'Ariane de Bugsnag ont tourné.
 */
export function createTelemetry(machine: string, options: TelemetryOptions): EngineTelemetry {
    const client = options.client ?? bugsnagClient
    const now = options.now ?? Date.now
    const trail = new TransitionTrail()

    const context = (): TelemetryMetadata => ({
        machine,
        role: options.role,
        platform: Platform.OS,
        appVersion: appVersion(),
    })

    return {
        transition(from, to, event, data) {
            const step = `${from} → ${to}`
            trail.push(`${step} (${event})`)
            client.leaveBreadcrumb(`${machine}: ${step}`, { event, ...cleanMetadata(data), ...context() })
        },

        error(step, cause, data) {
            const error = new EngineError(machine, step, cause)
            client.notify(error, {
                context: `engine/${machine}`,
                severity: "error",
                section: "engine",
                metadata: {
                    ...cleanMetadata(data),
                    ...describeCause(cause),
                    status: error.status,
                    step,
                    transitions: trail.list().join(" | "),
                    ...context(),
                },
            })
            return error
        },

        startOutage(kind, data) {
            const started = now()
            client.leaveBreadcrumb(`${machine}: ${kind} started`, { ...cleanMetadata(data), ...context() })
            let measure: OutageMeasure | null = null
            return {
                end(endData) {
                    if (measure != null) return measure
                    const ended = now()
                    measure = {
                        kind,
                        startedAt: new Date(started).toISOString(),
                        endedAt: new Date(ended).toISOString(),
                        durationMs: Math.max(0, ended - started),
                    }
                    client.leaveBreadcrumb(`${machine}: ${kind} ended`, {
                        ...cleanMetadata(endData),
                        durationMs: measure.durationMs,
                        ...context(),
                    })
                    return measure
                },
            }
        },
    }
}
